import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { AppDispatch, RootState } from 'store'
import { ColorVariant, Flex } from 'styling/GlobalStyles'
import { MediumText, SmallText } from 'styling/TypographyV2'
import {
  getOperationalSiteOrganogram,
  getSimpleOperationalSites,
} from 'features/operationalManagement/operationalManagementSlice'
import {
  IOperationalOrganogramTeam,
  IOperationalOrgUser,
  TEAM_TYPE_LABEL,
  TeamType,
  getTeamTypeAccent,
} from 'features/operationalManagement/operationalManagement.types'
import { Option, Select } from 'components/Select/Select'
import { OptionsProps, Table } from 'components/Table/Table'
import { TeamsIcon, UserSilhouetteIcon } from '../icons'
import {
  EmployeeAvatar,
  LegendItem,
  LegendRow,
  OrgStructureHeader,
  SelectedPersonCard,
  TreeChildrenRow,
  TreeNodeBox,
  TreeNodeCount,
  TreeNodeSubtitle,
  TreeNodeTitle,
  TreeNodeWrapper,
  TreeRoot,
  TreeScroll,
} from '../Organogram.styled'

const countAllMembers = (node: IOperationalOrganogramTeam): number => {
  let total = node.members.length
  for (const c of node.children) total += countAllMembers(c)
  return total
}

interface TreeNodeProps {
  team: IOperationalOrganogramTeam
  selectedId: number | null
  onSelect: (team: IOperationalOrganogramTeam) => void
  isRoot?: boolean
}

const TreeNode = ({ team, selectedId, onSelect, isRoot }: TreeNodeProps) => {
  const theme = useTheme() as any
  const accent = getTeamTypeAccent(theme, team.type)
  const typeLabel = TEAM_TYPE_LABEL[team.type]
  const total = countAllMembers(team)

  return (
    <TreeNodeWrapper className={isRoot ? 'root' : ''}>
      <TreeNodeBox
        accentColor={accent}
        selected={selectedId === team.id}
        onClick={() => onSelect(team)}
      >
        <TreeNodeTitle>{team.name}</TreeNodeTitle>
        <TreeNodeSubtitle>{typeLabel}</TreeNodeSubtitle>
        <TreeNodeCount>
          <TeamsIcon size={14} />
          {total} {total === 1 ? 'person' : 'people'}
        </TreeNodeCount>
      </TreeNodeBox>
      {team.children.length > 0 && (
        <TreeChildrenRow>
          {team.children.map((child) => (
            <TreeNode
              key={child.id}
              team={child}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </TreeChildrenRow>
      )}
    </TreeNodeWrapper>
  )
}

const findNode = (
  nodes: IOperationalOrganogramTeam[],
  id: number,
): IOperationalOrganogramTeam | null => {
  for (const n of nodes) {
    if (n.id === id) return n
    const found = findNode(n.children, id)
    if (found) return found
  }
  return null
}

export const OrgStructureTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const theme = useTheme() as any
  const { simpleSites, organogramBySiteId, loadingOrganogramSiteIds } = useSelector(
    (state: RootState) => state.operationalManagement,
  )

  const [siteId, setSiteId] = useState<number | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [highlightedMemberId, setHighlightedMemberId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(getSimpleOperationalSites())
  }, [dispatch])

  useEffect(() => {
    if (simpleSites.length === 0) return
    if (siteId == null) {
      setSiteId(simpleSites[0].id)
      return
    }
    // #21: if the selected site disappeared (e.g. after deletion/refresh), reset
    if (!simpleSites.some((s) => s.id === siteId)) {
      setSiteId(simpleSites[0].id)
    }
  }, [simpleSites, siteId])

  useEffect(() => {
    if (siteId == null || organogramBySiteId[siteId]) return
    // #24: abort the in-flight organogram fetch when siteId changes
    const promise = dispatch(getOperationalSiteOrganogram(siteId))
    return () => {
      promise.abort()
    }
  }, [dispatch, siteId, organogramBySiteId])

  useEffect(() => {
    setSelectedTeamId(null)
    setHighlightedMemberId(null)
  }, [siteId])

  const organogram = siteId != null ? organogramBySiteId[siteId] : undefined
  const loading = siteId != null && loadingOrganogramSiteIds.includes(siteId)

  const selectedTeam = useMemo<IOperationalOrganogramTeam | null>(() => {
    if (!organogram || selectedTeamId == null) return null
    return findNode(organogram.teams, selectedTeamId)
  }, [organogram, selectedTeamId])

  const siteOptions = useMemo<Option[]>(
    () => simpleSites.map((s) => ({ label: s.name, value: s.id })),
    [simpleSites],
  )
  const selectedSiteOption =
    siteId == null ? null : siteOptions.find((o) => o.value === siteId) ?? null

  const memberTableOptions = useMemo<OptionsProps<IOperationalOrgUser>[]>(
    () => [
      { header: 'Name', cell: (m) => `${m.firstName} ${m.lastName}` },
      { header: 'Department', cell: (m) => m.department?.name ?? '—' },
      { header: 'Role', cell: (m) => m.currentRole?.name ?? '—' },
    ],
    [],
  )

  const legendTypes: TeamType[] = useMemo(() => {
    const present = new Set<TeamType>()
    const walk = (n: IOperationalOrganogramTeam) => {
      present.add(n.type)
      n.children.forEach(walk)
    }
    organogram?.teams.forEach(walk)
    return Array.from(present).sort((a, b) => a - b)
  }, [organogram])

  const lead: IOperationalOrgUser | null = useMemo(() => {
    if (!selectedTeam) return null
    if (selectedTeam.supervisors.length > 0) {
      const u = selectedTeam.supervisors[0].user
      return {
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        department: null,
        currentRole: null,
      }
    }
    return selectedTeam.members[0] ?? null
  }, [selectedTeam])

  return (
    <>
      <OrgStructureHeader>
        <Flex column gap='0.5rem'>
          <div style={{ width: '300px' }}>
            <Select
              label='Select site'
              placeholder='Select a site'
              options={siteOptions}
              value={selectedSiteOption}
              onChange={(opt) =>
                setSiteId(opt.value == null ? null : (opt.value as number))
              }
            />
          </div>
        </Flex>
        {legendTypes.length > 0 && (
          <LegendRow>
            {legendTypes.map((t) => (
              <LegendItem key={t} accentColor={getTeamTypeAccent(theme, t)}>
                {TEAM_TYPE_LABEL[t]}
              </LegendItem>
            ))}
          </LegendRow>
        )}
      </OrgStructureHeader>

      <TreeScroll>
        {siteId == null ? (
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            Select a site to view its organisational structure.
          </SmallText>
        ) : loading && !organogram ? (
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            Loading…
          </SmallText>
        ) : organogram && organogram.teams.length === 0 ? (
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            No teams at this site yet.
          </SmallText>
        ) : organogram ? (
          <TreeRoot>
            <Flex gap='2rem' alignStart>
              {organogram.teams.map((root) => (
                <TreeNode
                  key={root.id}
                  team={root}
                  selectedId={selectedTeamId}
                  onSelect={(t) => {
                    setSelectedTeamId(t.id)
                    setHighlightedMemberId(null)
                  }}
                  isRoot
                />
              ))}
            </Flex>
          </TreeRoot>
        ) : null}
      </TreeScroll>

      {selectedTeam && (
        <>
          <SelectedPersonCard>
            <EmployeeAvatar>
              <UserSilhouetteIcon />
            </EmployeeAvatar>
            <Flex column>
              <Flex center gap='0.5rem'>
                <MediumText bold>
                  {lead
                    ? `${lead.firstName} ${lead.lastName}`
                    : 'No supervisor assigned'}
                </MediumText>
                <SmallText hexColor={getTeamTypeAccent(theme, selectedTeam.type)}>
                  • {selectedTeam.name}
                </SmallText>
              </Flex>
              <SmallText colorVariant={ColorVariant.SECONDARY}>
                {TEAM_TYPE_LABEL[selectedTeam.type]} ·{' '}
                {countAllMembers(selectedTeam)} people
              </SmallText>
            </Flex>
          </SelectedPersonCard>

          <Table<IOperationalOrgUser>
            cellKey='id'
            items={selectedTeam.members}
            emptyListOptions={{ title: 'No members in this team.' }}
            onClick={(m) => setHighlightedMemberId(m.id)}
            isRowHighlighted={(m) => highlightedMemberId === m.id}
            options={memberTableOptions}
          />
        </>
      )}
    </>
  )
}
